import React, { useState, useMemo, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Label,
  Input,
} from 'reactstrap';
import FormInput from '../../common/FormInput';
import SelectInput from '../../common/SelectInput';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import DescriptionModal from '../../modals/descriptionModals/DescriptionModal';
import AssociateVendorModal from '../../modals/vendorModals/AssociateVendorModal';
import BarcodeModal from '../../modals/BarcodeModal';
import ParentProductModal from '../../modals/ParentProductModal';
import { useProductLov } from 'features/products/hooks/useProductLov';
import LoadingIndicator from 'Components/Loading/LoadingIndicator';
import { VendorData } from 'features/organizations';
import { useFormSection } from 'features/products/context/ProductDetailContext';
import { ParentProductData } from 'features/products/types/types';

const FlatpickrComponent = Flatpickr as any;

// Helper function to safely parse dates
const safeParseDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  
  const parsedDate = new Date(dateValue);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const ProductProfile: React.FC = () => {
  // Use form context instead of props
  const { watch, setValue, getError } = useFormSection('productProfile');
  
  
  // State for parent product name display
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [parentProductName, setParentProductName] = useState('');
  
  // Use the new LOV system
  const {
    isReady,
    isLoading,
    isProcessing,
    error,
    retry,
    statusOptions,
    typeOptions,
    categoryOptions,
    uomOptions,
    marginTypeOptions,
    getSubTypesForType,
    getSubCategoriesForCategory,
  } = useProductLov();

  // Watch form values for hierarchical filtering
  const type = watch('type');
  const category = watch('category');
  const subType = watch('subType');
  const subCategory = watch('subCategory');

  // Find selected objects for hierarchical filtering
  const selectedTypeObj = typeOptions.find(option => option.label === type);
  const selectedCategoryObj = categoryOptions.find(option => option.label === category);

  // Get filtered subtypes and subcategories
  const subTypeOptions = useMemo(() => {
    if (!selectedTypeObj) return [];
    return getSubTypesForType(selectedTypeObj.value).map(item => ({
      value: item.rowId,
      label: item.name
    }));
  }, [selectedTypeObj, getSubTypesForType]);

  const subCategoryOptions = useMemo(() => {
    if (!selectedCategoryObj) return [];
    return getSubCategoriesForCategory(selectedCategoryObj.value).map(item => ({
      value: item.rowId,
      label: item.name
    }));
  }, [selectedCategoryObj, getSubCategoriesForCategory]);

  // Clear subtype when type changes
  useEffect(() => {
    if (type && selectedTypeObj) {
      // Check if current subtype is still valid for the selected type
      const isValidSubtype = subTypeOptions.some(option => option.label === subType);
      if (!isValidSubtype && subType) {
        setValue('subType', '');
      }
    }
  }, [type, selectedTypeObj, subTypeOptions, subType, setValue]);

  // Clear subcategory when category changes
  useEffect(() => {
    if (category && selectedCategoryObj) {
      // Check if current subcategory is still valid for the selected category
      const isValidSubcategory = subCategoryOptions.some(option => option.label === subCategory);
      if (!isValidSubcategory && subCategory) {
        setValue('subCategory', '');
      }
    }
  }, [category, selectedCategoryObj, subCategoryOptions, subCategory, setValue]);


  const [isDescModalOpen, setIsDescModalOpen] = useState(false);
  const [isDescLongModalOpen, setIsDescLongModalOpen] = useState(false);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [isBarcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [isParentProductModalOpen, setIsParentProductModalOpen] = useState(false);

  const handleOpenDescModal = () => setIsDescModalOpen(true);
  const handleCloseDescModal = () => setIsDescModalOpen(false);
  const handleSaveDescription = (newDesc: string) => {
    setValue('description', newDesc);
    setIsDescModalOpen(false);
  };

  const handleOpenDescLongModal = () => setIsDescLongModalOpen(true);
  const handleCloseDescLongModal = () => setIsDescLongModalOpen(false);
  const handleSaveDescriptionLong = (newDesc: string) => {
    setValue('longDescription', newDesc);
    setIsDescLongModalOpen(false);
  };

  // vendor modal functions
  const handleOpenVendorModal = () => setIsVendorModalOpen(true);
  const handleCloseVendorModal = () => setIsVendorModalOpen(false);
  const handleSelectVendor = (vendor: VendorData) => {
    setValue('vendorName', vendor.name);
    setValue('vendorId', vendor.rowId);
    setIsVendorModalOpen(false);
  };

  // parent product modal functions
  const handleOpenParentProductModal = () => setIsParentProductModalOpen(true);
  const handleCloseParentProductModal = () => setIsParentProductModalOpen(false);
  const handleSelectParentProduct = (product: ParentProductData) => {
    // Only set the parent product reference, don't overwrite current product details
    setValue('parRowId', product.rowId);
    setValue('parentProductName', product.productName || '');
    setParentProductName(product.productName || '');
    setIsParentProductModalOpen(false);
  };

  // Show loading state
  if (!isReady) {
    return (
      <fieldset className="border rounded p-2">
        <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.9rem' }}>Product Profile</legend>
        <LoadingIndicator
          isLoading={isLoading}
          isProcessing={isProcessing}
          error={error}
          title="Loading product options from server..."
        />
        {error && (
          <button className="btn btn-outline-primary btn-sm mt-2" onClick={retry}>
            Retry Loading Options
          </button>
        )}
      </fieldset>
    );
  }

  return (
    <fieldset className="border rounded p-2">
      <legend className="float-none w-auto px-2 mb-0 text-body" style={{ fontSize: '0.9rem' }}>Product Profile</legend>
      <Form>
        <Row>
          <Col sm={4}>
            <FormInput
              id="name"
              label="Name"
              value={watch('name') || ''}
              onChange={(value: string) => setValue('name', value)}
              required
            />
            {/* Display validation error if any */}
            {getError('name') && (
              <div className="text-danger small mt-1">
                {getError('name')?.message}
              </div>
            )}
          </Col>
          <Col sm={2}>
            <FormInput
              id="productId"
              label="Product Id"
              value={watch('productId')?.toString() || ''}
              onChange={(value: string) => setValue('productId', Number(value))}
              readOnly
            />
          </Col>
          <Col sm={2}>
            <FormInput
              id="vendorName"
              label="Vendor"
              value={watch('vendorName') || ''}
              onChange={(value: string) => setValue('vendorName', value)}
              hasSearchButton
              onSearchClick={handleOpenVendorModal}
            />
            {/* Display validation error if any */}
            {getError('vendorName') && (
              <div className="text-danger small mt-1">
                {getError('vendorName')?.message}
              </div>
            )}
          </Col>
          <Col sm={2}>
            <FormInput
              id="productCode"
              label="Product Code"
              value={watch('productCode') || ''}
              onChange={(value: string) => setValue('productCode', value)}
            />
            {/* Display validation error if any */}
            {getError('productCode') && (
              <div className="text-danger small mt-1">
                {getError('productCode')?.message}
              </div>
            )}
          </Col>
          <Col sm={2}>
            <FormInput
              id="venProdCode"
              label="V. Product Code"
              value={watch('venProdCode') || ''}
              onChange={(value: string) => setValue('venProdCode', value)}
            />
          </Col>
          <Col sm={2}>
            <FormInput
              id="parentProductName"
              label="Parent Product"
              value={watch('parentProductName') || ''}
              onChange={() => {}} // No-op since this is read-only and set via modal
              hasSearchButton
              onSearchClick={handleOpenParentProductModal}
              readOnly
            />
          </Col>
          <Col sm={2}>
            <FormInput
              id="barcode"
              label="Barcode"
              value={watch('barcode') || ''}
              onChange={(value: string) => setValue('barcode', value)}
              hasSearchButton
              onSearchClick={() => setBarcodeModalOpen(true)}
            />
          </Col>
          <Col sm={2}>
            <SelectInput
              formData={{ type: watch('type') }}
              setFormData={(data: { type: string }) => setValue('type', data.type)}
              label="Type"
              id="type"
              options={typeOptions.map(option => option.label)}
            />
          </Col>
          <Col sm={2}>
            <SelectInput
              formData={{ status: watch('status') }}
              setFormData={(data: { status: string }) => setValue('status', data.status)}
              label="Status"
              id="status"
              htmlId="statusValue"
              options={statusOptions.map(option => option.label)}
            />
          </Col>
          <Col sm={2}>
            <SelectInput
              formData={{ subType: watch('subType') }}
              setFormData={(data: { subType: string }) => setValue('subType', data.subType)}
              label="Subtype"
              id="subType"
              options={subTypeOptions.map(option => option.label)}
              disabled={!watch('type')}
            />
          </Col>

          <Col sm={2}>
            <SelectInput
              formData={{ uom: watch('uom') }}
              setFormData={(data: { uom: string }) => setValue('uom', data.uom)}
              label="UOM"
              id="uom"
              options={uomOptions.map(option => option.label)}
            />
          </Col>
          <Col sm={2}>
            <SelectInput
              formData={{ category: watch('category') }}
              setFormData={(data: { category: string }) => setValue('category', data.category)}
              label="Category"
              id="category"
              options={categoryOptions.map(option => option.label)}
            />
          </Col>
          <Col sm={2}>
            <FormInput
              id="capacity"
              label="Capacity"
              value={watch('capacity') || ''}
              onChange={(value: string) => setValue('capacity', value)}
            />
          </Col>

          <Col sm={2}>
            <SelectInput
              formData={{ subCategory: watch('subCategory') }}
              setFormData={(data: { subCategory: string }) => setValue('subCategory', data.subCategory)}
              label="Subcategory"
              id="subCategory"
              options={subCategoryOptions.map(option => option.label)}
              disabled={!watch('category')}
            />
          </Col>
          <Col sm={2}>
            <SelectInput
              formData={{ marginType: watch('marginType') }}
              setFormData={(data: { marginType: string }) => setValue('marginType', data.marginType)}
              label="Margin Type"
              id="marginType"
              options={marginTypeOptions.map(option => option.label)}
            />
          </Col>

          <Col sm={2}>
            <div className="mb-1">
              <Label className="form-label mb-0">Start Date</Label>
              <FlatpickrComponent
                className="form-control form-control-sm"
                options={{
                  dateFormat: 'd/m/Y',
                }}
                value={safeParseDate(watch('startDate'))}
                onChange={([date]: Date[]) =>
                  setValue('startDate', date.toISOString())
                }
              />
            </div>
          </Col>

          <Col sm={4}>
            <Row>
              <Col sm={6}>
                <FormInput
                  id="description"
                  label="Short.."
                  value={watch('description') || ''}
                  onChange={(value: string) => setValue('description', value)}
                  hasSearchButton
                  onSearchClick={handleOpenDescModal}
                />
              </Col>
              <Col sm={6}>
                <FormInput
                  id="longDescription"
                  label="Long..."
                  value={watch('longDescription') || ''}
                  onChange={(value: string) => setValue('longDescription', value)}
                  hasSearchButton
                  onSearchClick={handleOpenDescLongModal}
                />
              </Col>
            </Row>
          </Col>

          <Col sm={2}>
            <div className="mb-1">
              <Label className="form-label mb-0">End Date</Label>
              <FlatpickrComponent
                className="form-control form-control-sm"
                options={{
                  dateFormat: 'd/m/Y',
                }}
                value={safeParseDate(watch('endDate'))}
                onChange={([date]: Date[]) =>
                  setValue('endDate', date.toISOString())
                }
              />
            </div>
          </Col>

          <Col sm={2}>
            <div className="mb-1">
              <Label className="form-label mb-0">N. Avl Date</Label>
              <FlatpickrComponent
                className="form-control form-control-sm"
                options={{
                  dateFormat: 'd/m/Y',
                }}
                value={null}
                onChange={([date]: Date[]) =>
                  setValue('nextAvlDate', date.toISOString())
                }
              />
            </div>
          </Col>

          <Col sm={2}>
            <FormInput
              id="standardPrice"
              label="Std Price"
              value={watch('standardPrice')?.toString() || ''}
              onChange={(value: string) => setValue('standardPrice', value)}
              type="number"
            />
            {/* Display validation error if any */}
            {getError('standardPrice') && (
              <div className="text-danger small mt-1">
                {getError('standardPrice')?.message}
              </div>
            )}
          </Col>

          <Col sm={2}>
            <div className="mb-1">
              <Label className="form-label mb-0">Active</Label>
              <div className="form-check form-switch">
                <Input
                  className="form-check-input"
                  type="switch"
                  id="activeFlag"
                  checked={watch('activeFlag') || false}
                  onChange={(e) => setValue('activeFlag', e.target.checked)}
                />
              </div>
            </div>
          </Col>

          <Col sm={2}>
            <div className="mb-1">
              <Label className="form-label mb-0">Allocation Only</Label>
              <div className="form-check form-switch">
                <Input
                  className="form-check-input"
                  type="switch"
                  id="allocationOnly"
                  checked={watch('allocationOnly') || false}
                  onChange={(e) => setValue('allocationOnly', e.target.checked)}
                />
              </div>
            </div>
          </Col>

          <Col sm={2}>
            <div className="mb-1">
              <Label className="form-label mb-0">Orderable</Label>
              <div className="form-check form-switch">
                <Input
                  className="form-check-input"
                  type="switch"
                  id="orderable"
                  checked={watch('orderable') || false}
                  onChange={(e) => setValue('orderable', e.target.checked)}
                />
              </div>
            </div>
          </Col>

          <Col sm={2}>
            <div className="mb-1">
              <Label className="form-label mb-0">Taxable</Label>
              <div className="form-check form-switch">
                <Input
                  className="form-check-input"
                  type="switch"
                  id="taxable"
                  checked={watch('taxable') || false}
                  onChange={(e) => setValue('taxable', e.target.checked)}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Form>

      <DescriptionModal
        isOpen={isDescModalOpen}
        onClose={handleCloseDescModal}
        onSave={handleSaveDescription}
        initialValue={watch('description') || ''}
      />

      <DescriptionModal
        isOpen={isDescLongModalOpen}
        onClose={handleCloseDescLongModal}
        onSave={handleSaveDescriptionLong}
        initialValue={watch('longDescription') || ''}
      />

      <AssociateVendorModal
        isOpen={isVendorModalOpen}
        onClose={handleCloseVendorModal}
        onSelect={handleSelectVendor}
      />

      <BarcodeModal
        isOpen={isBarcodeModalOpen}
        onClose={() => setBarcodeModalOpen(false)}
      />

      <ParentProductModal
        isOpen={isParentProductModalOpen}
        onClose={handleCloseParentProductModal}
        onSelect={handleSelectParentProduct}
      />
    </fieldset>
  );
};

export default ProductProfile;
